<template>
<profile-layout>
  <template slot="sidebar">
    <sidebar-modal :title="sidebarTitle"
      :active.sync="isSidebarActive"
      :button="sidebarButton"
      :loading="sidebarLoading"
      :destructive="sidebarDestructiveButton"
      :destroyLoading="sidebarDestroyLoading"
      :message.sync="sidebarMessage"
      :error.sync="sidebarError"
      v-on:action="onSidebarAction"
      v-on:destroy="onSidebarDestroy">
      <component v-if="isSidebarActive" v-bind:is="sidebarComponent" :toggleResumeModal="toggleResumeModal"></component>
    </sidebar-modal>
  </template>
  <template slot="top">
    <dropdown-modal :title="this.$t('jobs.areYouSure')" :active.sync="resumeModal">
      <div>
        <p>{{this.$t('signup.areYouSureYou')}}</p>
        <div>
          <button class="button is-danger w-100" @click="deleteResume"> <spinner size="small" v-if="resumeLoading"></spinner> <p v-else>{{this.$t('profile.delete')}}</p></button>
        </div>
      </div>
    </dropdown-modal>
  </template>
  <template slot="left">
    <div class="section">
      <user-details-card
        :user="user"
        :coverPictureUrl="coverPictureUrl"
        :editable="true"
        v-on:edit-profile="onEditProfile"
        v-on:logout="onLogout"
      />
    </div>
    <div class="section">
      <external-profile-section @add="onAddExternalProfiles"></external-profile-section>
    </div>
    <div class="section">
      <experience-section v-on:add="onAddExperience"
                          v-on:edit="onEditExperience" />
    </div>
    <div class="section">
      <skills-section v-on:add="onEditSkills" v-on:edit="onEditSkills" />
    </div>
  </template>
  <template slot="right">
    <div class="jobseeker-card is-fixed">
      <jobseeker-card :profile="jobseekerProfile" :user="user"
        :isOther="false" v-on:view="onViewJobseeker" :editable="true" />
    </div>
  </template>
</profile-layout>
</template>

<script>
import ProfileLayout from '@/templates/profile-layout'
import UserDetailsCard from '../../components/profile/user-details-card.vue'
import JobseekerCard from '../../components/jobseekers/jobseeker-card.vue'
import SidebarModal from '@/components/sidebar-modal'
import EditProfile from '@/components/profile/edit-profile'
import EditJobseeker from '@/components/jobseekers/edit-jobseeker'
import ExperienceSection from '@/components/profile/experience-section'
import AddExperience from '@/components/profile/add-experience'
import EditExperience from '@/components/profile/edit-experience'
import JobseekerPreferences from '@/components/jobseekers/preferences'
import SkillsSection from '../../components/profile/skills/skills-section.vue'
import EditSkills from '@/components/profile/skills/edit-skills'
import ExternalProfileSection from '@/components/profile/profiles/profile-section'
import AddExternalProfiles from '@/components/profile/profiles/add-profile'
import DropdownModal from '@/components/dropdown-modal'
import Jobseekers from '@/api/jobseekers'
import Spinner from 'vue-simple-spinner'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

export default {
  components: {
    UserDetailsCard,
    JobseekerCard,
    SidebarModal,
    EditProfile,
    ExperienceSection,
    AddExperience,
    EditExperience,
    JobseekerPreferences,
    EditJobseeker,
    SkillsSection,
    EditSkills,
    ProfileLayout,
    ExternalProfileSection,
    AddExternalProfiles,
    DropdownModal,
    Spinner
  },
  methods: {
    ...mapActions('users', [
      'getUser',
      'updateUser',
      'createOrUpdateSocialProfiles',
      'getUserProfiles'
    ]),
    ...mapActions('jobseekerProfiles', [
      'getJobseekerProfile',
      'updateJobseekerProfile'
    ]),
    ...mapActions('profileForm', [
      'loadWithUser'
    ]),
    ...mapMutations('profileForm', [
      'setFirstNameError', 'setLastNameError', 'setCompanyNameError'
    ]),
    ...mapActions('experience', [
      'createExperience',
      'updateExperience',
      'deleteExperience'
    ]),
    ...mapMutations('experienceForm', {
      resetExperienceFormData: 'resetData',
      loadExperienceForm: 'loadWithExperience'
    }),
    ...mapMutations('jobseekerForm', {
      resetJobseekerFormData: 'resetData'
    }),
    ...mapActions('skills', [
      'loadEditSkills',
      'updateSkills'
    ]),
    ...mapActions('app', [
      'logout'
    ]),
    ...mapActions('jobseekerForm', [
      'loadWithProfile'
    ]),
    async onLogout () {
      await this.logout()
      this.$router.push('/login')
    },
    toggleResumeModal () {
      this.resumeModal = !this.resumeModal
    },
    async deleteResume () {
      try {
        this.resumeLoading = true
        const res = await Jobseekers.deleteResume()
        if (res) {
          this.getJobseekerProfile()
        }
        this.resumeLoading = false
        this.resumeModal = false
      } catch (e) {
        this.resumeLoading = false
      }
    },
    onAddExternalProfiles () {
      this.$ga.event('Profile', 'Add Social Profiles Clicked')
      this.sidebarDestructiveButton = null
      this.sidebarButton = this.$t('profile.saveSocialProfiles')
      this.sidebarTitle = this.$t('profile.socialProfiles')
      this.sidebarComponent = 'AddExternalProfiles'
      this.isSidebarActive = true
    },
    onEditSkills () {
      this.$ga.event('Profile', 'Edit Skills Clicked')
      this.loadEditSkills()
      this.sidebarDestructiveButton = null
      this.sidebarButton = this.$t('profile.saveTags')
      this.sidebarTitle = this.$t('profile.tags')
      this.sidebarComponent = 'EditSkills'
      this.isSidebarActive = true
    },
    onEditProfile () {
      this.$ga.event('Profile', 'Edit Profile Clicked')
      this.sidebarDestructiveButton = null
      this.sidebarComponent = 'EditProfile'
      this.sidebarTitle = this.$t('profile.editProfile')
      this.sidebarButton = this.$t('profile.saveProfile')
      this.isSidebarActive = true
    },
    onAddExperience () {
      this.$ga.event('Profile', 'Add Experience Clicked')
      this.sidebarDestructiveButton = null
      this.resetExperienceFormData()
      this.sidebarComponent = 'AddExperience'
      this.sidebarTitle = this.$t('profile.addExperience')
      this.sidebarButton = this.$t('profile.saveExperience')
      this.isSidebarActive = true
    },
    onEditExperience (experience) {
      this.$ga.event('Profile', 'Edit Experience Clicked')
      this.resetExperienceFormData()
      this.loadExperienceForm(experience)
      this.sidebarComponent = 'EditExperience'
      this.sidebarTitle = this.$t('profile.editExperience')
      this.sidebarButton = this.$t('profile.save')
      this.sidebarDestructiveButton = this.$t('profile.delete')
      this.isSidebarActive = true
    },
    onViewJobseeker () {
      this.sidebarDestructiveButton = null
      this.sidebarComponent = 'EditJobseeker'
      this.sidebarTitle = this.$t('jobs.jobPreferences')
      this.sidebarButton = this.$t('jobs.saveJobPreferences')
      this.isSidebarActive = true
    },
    validations () {
      if (this.firstName.length === 0) {
        this.setFirstNameError(this.$t('profile.firstNameRequired'))
        return false
      } else if (this.lastName.length === 0) {
        this.setLastNameError(this.$t('profile.lastNameRequired'))
        return false
      } else if (this.companyName.length === 0) {
        this.setCompanyNameError(this.$t('profile.companyNameRequired'))
        return false
      }
      return true
    },
    async onSidebarAction () {
      switch (this.sidebarComponent) {
        case 'EditProfile': {
          const validations = this.validations()
          if (!validations) {
            return false
          }
          this.sidebarLoading = true
          const data = this.profileFormData
          try {
            await this.updateUser(data)
            this.loadWithUser(this.user)
            this.isSidebarActive = false
          } catch (error) {
            this.sidebarError = error.message
          }
          this.sidebarLoading = false
          break
        }
        case 'AddExperience': {
          const data = this.experienceFormData
          if (data.from.month === 0) { return }
          if (!data.currentPosition && data.to.month === 0) { return }
          this.sidebarLoading = true
          try {
            await this.createExperience(data)
            this.isSidebarActive = false
            this.sidebarLoading = false
          } catch (error) {
            this.sidebarError = error.message
          }
          break
        }
        case 'EditExperience': {
          const data = this.experienceFormData
          const current = this.currentExperience
          if (data.from.month === 0 || !current) { return }
          if (!data.currentPosition && data.to.month === 0) { return }
          this.sidebarLoading = true
          try {
            await this.updateExperience({
              data: data,
              id: current.id
            })
            this.isSidebarActive = false
          } catch (error) {
            this.sidebarError = error.message
          }
          this.sidebarLoading = false
          break
        }
        case 'JobseekerPreferences': {
          this.sidebarComponent = 'EditJobseeker'
          this.sidebarTitle = this.$t('jobs.jobPreferences')
          this.sidebarButton = this.$t('jobs.saveJobPreferences')
          break
        }
        case 'EditJobseeker': {
          const data = this.jobseekerFormData
          this.sidebarLoading = true
          try {
            await this.updateJobseekerProfile(data)
            this.isSidebarActive = false
          } catch (error) {
            this.sidebarError = error.message
          }
          this.sidebarLoading = false
          break
        }
        case 'EditSkills': {
          this.sidebarLoading = true
          try {
            await this.updateSkills()
            this.isSidebarActive = false
          } catch (error) {
            this.sidebarError = error.message
          }
          this.sidebarLoading = false
          break
        }
        case 'AddExternalProfiles': {
          this.sidebarLoading = true
          try {
            await this.createOrUpdateSocialProfiles()
            this.sidebarMessage = this.$t('profile.savedSuccessfully')
          } catch (err) {
            this.sidebarError = err.message
          }
          this.sidebarLoading = false
          break
        }
        default:
          break
      }
    },
    async onSidebarDestroy () {
      switch (this.sidebarComponent) {
        case 'EditExperience': {
          this.sidebarDestroyLoading = true
          const current = this.currentExperience
          if (!current) { return }
          try {
            await this.deleteExperience(current.id)
            this.isSidebarActive = false
          } catch (error) {
          }
          this.sidebarDestroyLoading = false
          break
        }
        default: {
          break
        }
      }
    }
  },
  computed: {
    ...mapGetters('users', [
      'coverPictureUrl',
      'user',
      'fullName'
    ]),
    ...mapState('jobseekerProfiles', [
      'jobseekerProfile'
    ]),
    ...mapGetters('profileForm', {
      profileFormData: 'formData'
    }),
    ...mapState('profileForm', [
      'firstName', 'lastName', 'companyName'
    ]),
    ...mapState('experienceForm', [
      'currentExperience'
    ]),
    ...mapGetters('experienceForm', {
      experienceFormData: 'formData'
    }),
    ...mapGetters('jobseekerForm', {
      jobseekerFormData: 'formData'
    })
  },
  data () {
    return {
      sidebarTitle: '',
      isSidebarActive: false,
      sidebarComponent: '',
      sidebarButton: null,
      sidebarLoading: false,
      sidebarDestructiveButton: null,
      sidebarDestroyLoading: false,
      showBanner: false,
      sidebarMessage: null,
      sidebarError: null,
      resumeModal: false,
      resumeLoading: false
    }
  },
  async mounted () {
    await Promise.all([
      await this.getUser(),
      this.getJobseekerProfile()
    ])
    await this.getJobseekerProfile()
    this.loadWithProfile(this.jobseekerProfile)
    this.$store.dispatch('users/getUserProfiles', this.user.id)
  }
}
</script>

<style lang="scss" scoped>

div.section:first-child {
  padding: 0;
}

div.section {
  padding-left: 0;
  padding-right: 0;
  padding-bottom: 0;
  padding-top: 2.5rem;
}

.card {
  box-shadow: none;
}

.w-100{
  width: 100%
}

</style>



// WEBPACK FOOTER //
// src/pages/profiles/jobseeker-profile.vue